import React, { useState, useEffect } from "react";
import { useUsers } from "hooks/useUsers";
import { useUsersSearch } from "hooks/useUsersSearch";
import UsersHeader from "components/users/UsersHeader";
import UsersTableView from "components/users/UsersTableView";
import UsersCardView from "components/users/UsersCardView";
import UsersEmptyState from "components/users/UsersEmptyState";
import UsersPagination from "components/users/UsersPagination";
import AddUserDialog from "components/users/AddUserDialog";
import { PageError } from "components/shared/PageError";
import { searchEmpty } from "utils/emptyCopy";
import { PageContainer } from "styles/components/usersList.styles";

const UsersList: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [addUserVisible, setAddUserVisible] = useState(false);
  const [sortField] = useState("updatedAt");
  const [sortOrder] = useState("DESC");

  const { searchValue, searchInputValue, handleSearchChange } =
    useUsersSearch(setCurrentPage);

  const { data, isLoading, error, refetch } = useUsers(
    currentPage,
    pageSize,
    sortField,
    sortOrder,
    searchValue,
  );

  useEffect(() => {
    if (data?.pagination) {
      setTotalRecords(data.pagination.totalItems);
    }
  }, [data]);

  const onPageChange = (event: {
    first: number;
    rows: number;
    page: number;
  }) => {
    setCurrentPage(event.page + 1);
    setPageSize(event.rows);
  };

  if (error) {
    return (
      <PageContainer>
        <PageError
          title="Error loading users"
          message="Please try again later."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  const users = data?.response || [];
  const showEmpty = !isLoading && users.length === 0;

  return (
    <PageContainer>
      <UsersHeader
        title="Users Management"
        onAddUser={() => setAddUserVisible(true)}
        searchValue={searchInputValue}
        onSearchChange={handleSearchChange}
      />

      {showEmpty ? (
        <UsersEmptyState
          isLoading={false}
          message={
            searchValue
              ? searchEmpty("users")
              : "No users found. Try adjusting your search criteria or add a new user."
          }
          actionLabel="+ Add User"
          onAction={() => setAddUserVisible(true)}
        />
      ) : (
        <>
          <UsersTableView users={users} isLoading={isLoading} />
          <UsersCardView users={users} isLoading={isLoading} />
        </>
      )}

      <UsersPagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
        showPagination={!!data?.pagination && users.length > 0}
      />

      <AddUserDialog
        visible={addUserVisible}
        onHide={() => setAddUserVisible(false)}
      />
    </PageContainer>
  );
};

export default UsersList;
